import { IconArrowRight, IconBuilding, IconChecks, IconChevronDown, IconPaperclip } from "@tabler/icons-react";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useState, type ReactNode } from "react";
import { TERMINAL_NEGATIVOS } from "@/components/solicitudes/stepper/steps-config";
import { Badge, Card, Divider, Group, Stack, Text, ThemeIcon } from "@/components/ui";
import { EstadoBadge } from "@/components/ui/EstadoBadge";
import { etiquetaEstado } from "@/lib/estado-labels";
import type { MiSolicitud } from "@/lib/types";
import { formatFecha } from "@/shared/format/fecha";
import { formatearMonto } from "@/shared/format/money";
import { sustantivoDetalle, tipoDetalleDe } from "../utils/detalle-expandible";
import { FacturasDeSolicitudMobile } from "./FacturasDeSolicitudMobile";
import { InvoiceDeSolicitudMobile } from "./InvoiceDeSolicitudPanel";
import { BotonInfoCaOc } from "./BotonInfoCaOc";

/** Card mobile · vista alternativa a la tabla desktop. */
export function SolicitudCardMobile({ s, expandirTodas = false }: { s: MiSolicitud; expandirTodas?: boolean }) {
  const navigate = useNavigate();
  // Equivalente mobile de la fila expandible de la tabla · las facturas se piden al abrir.
  const [verFacturas, setVerFacturas] = useState(false);
  // Solo se ofrece "Ver facturas"/"Ver invoice" si hay algo que mostrar (mismo criterio que el chevron de la tabla).
  const tipoDetalle = tipoDetalleDe(s);
  const tieneFacturas = tipoDetalle !== null;
  const facturasId = `facturas-solicitud-mobile-${s.id}`;
  // Switch «Mostrar facturas» de la página · abre/cierra el detalle de todas las tarjetas.
  useEffect(() => {
    setVerFacturas(expandirTodas && tieneFacturas);
  }, [expandirTodas, tieneFacturas]);
  const accent = s.tieneRechazoPendiente ? "advertencia" : s.enviadaAContabilizacion ? "exito" : "multiva";

  return (
    <Card padding="xs" className="overflow-hidden p-0" style={{ borderLeft: `3px solid var(--color-${accent}-500)` }}>
      <div className="p-4">
        <Stack gap="md">
          <CardHeader s={s} />
          <OrganizacionRow nombre={s.organizacionNombre} />
          <div className="grid grid-cols-2 gap-3">
            <CampoLabel label="Área" valor={s.areaNombre} />
            <CampoLabel label="Tipo" valor={s.tipo} sub={s.subtipo} />
            {s.claveCaOc && (
              <CampoLabel
                label="CA/OC"
                valor={s.claveCaOc}
                mono
                accion={<BotonInfoCaOc tipo={s.tipo} clave={s.claveCaOc} />}
              />
            )}
            <CampoLabel
              label="Proveedor"
              valor={s.proveedorRazonSocial}
              sub={s.proveedorNumero !== null ? `Núm. ${s.proveedorNumero}` : null}
            />
            <CampoLabel label="RFC emisor" valor={s.rfcEmisor} mono />
            <CampoLabel
              label="Descripción"
              valor={
                s.descripcionPrimerConcepto && s.descripcionPrimerConcepto.length > 25
                  ? s.descripcionPrimerConcepto.slice(0, 25) + "…"
                  : s.descripcionPrimerConcepto
              }
            />
            {s.fechaPago && <CampoLabel label="Fecha de pago" valor={formatFecha(s.fechaPago)} />}
          </div>
          <TotalDestacado total={s.totalCfdi} moneda={s.monedaCfdi} />
          <ChipsMeta s={s} />
        </Stack>
      </div>
      <Divider />
      <div className={`grid divide-x divide-border ${tieneFacturas ? "grid-cols-2" : "grid-cols-1"}`}>
        {tieneFacturas && (
        <button
          type="button"
          aria-expanded={verFacturas}
          aria-controls={verFacturas ? facturasId : undefined}
          onClick={() => setVerFacturas((v) => !v)}
          className="flex w-full items-center justify-center gap-1.5 px-4 py-3 text-[13px] font-medium text-multiva-700 transition-colors hover:bg-muted"
        >
          {`${verFacturas ? "Ocultar" : "Ver"} ${tipoDetalle ? sustantivoDetalle(tipoDetalle) : ""}`}
          <IconChevronDown
            size={14}
            stroke={1.7}
            className={`transition-transform duration-150 motion-reduce:transition-none ${verFacturas ? "rotate-180" : ""}`}
          />
        </button>
        )}
        <button
          type="button"
          onClick={() => navigate({ to: "/solicitudes/$id", params: { id: s.id } })}
          className="flex w-full items-center justify-center gap-1.5 px-4 py-3 text-[13px] font-medium text-multiva-700 transition-colors hover:bg-muted"
        >
          Ver detalle
          <IconArrowRight size={14} stroke={1.7} />
        </button>
      </div>
      {verFacturas &&
        (tipoDetalle === "Invoice" ? (
          <InvoiceDeSolicitudMobile id={facturasId} solicitudId={s.id} folio={s.folio} />
        ) : (
          <FacturasDeSolicitudMobile id={facturasId} solicitudId={s.id} />
        ))}
    </Card>
  );
}

function CardHeader({ s }: { s: MiSolicitud }) {
  return (
    <Group justify="space-between" align="flex-start" wrap="nowrap">
      <Stack gap={6} className="min-w-0 flex-1">
        <Group gap={8} wrap="nowrap">
          {s.tieneRechazoPendiente && (
            <span
              className="size-2 shrink-0 rounded-full"
              style={{
                backgroundColor: "var(--color-advertencia-500)",
                boxShadow: "0 0 0 4px color-mix(in srgb, var(--color-advertencia-500) 25%, transparent)",
              }}
            />
          )}
          <Text fw={700} ff="mono" size="md">
            {s.folio}
          </Text>
        </Group>
        <EstadoBadge
          estado={s.estado}
          label={etiquetaEstado(s.estado, { nivelActual: s.flujoNivelActual, rolActual: s.flujoRolActual })}
        />
        {(s.cantidadDocumentos ?? 0) > 0 && (
          <Group
            gap={6}
            wrap="nowrap"
            aria-label="Archivos de soporte documental"
            className="self-start rounded-md border px-2.5 py-1"
            style={{
              backgroundColor: "color-mix(in srgb, var(--color-multiva-600) 12%, transparent)",
              borderColor: "color-mix(in srgb, var(--color-multiva-600) 30%, transparent)",
            }}
          >
            <IconPaperclip size={12} stroke={1.7} className="text-multiva-600" />
            <Text size="xs" fw={500} c="multiva-700" className="text-[11px] leading-tight">
              {s.cantidadDocumentos} archivo{s.cantidadDocumentos === 1 ? "" : "s"} de soporte documental
            </Text>
          </Group>
        )}
      </Stack>
      <Stack gap={0} align="flex-end" className="shrink-0">
        <Text size="xs" c="dimmed" fw={600} className="text-[10px] tracking-wide">
          {formatFecha(s.fechaUltimoEvento)}
        </Text>
        <Text size="xs" c="dimmed" className="text-[10px]">
          {new Date(s.fechaUltimoEvento).toLocaleTimeString("sv-SE", { hour: "2-digit", minute: "2-digit" })}
        </Text>
      </Stack>
    </Group>
  );
}

function OrganizacionRow({ nombre }: { nombre: string | null }) {
  return (
    <Group gap="sm" wrap="nowrap" align="center">
      <ThemeIcon variant="filled" color="multiva" size="md">
        <IconBuilding size={16} stroke={1.6} />
      </ThemeIcon>
      <div className="min-w-0 flex-1">
        <Text size="xs" c="dimmed" tt="uppercase" fw={600} className="text-[10px] tracking-wide">
          Organización
        </Text>
        <Text size="sm" fw={500} truncate>
          {nombre ?? ""}
        </Text>
      </div>
    </Group>
  );
}

function CampoLabel({
  label,
  valor,
  sub,
  mono,
  accion,
}: {
  label: string;
  valor: string | null;
  sub?: string | null;
  mono?: boolean;
  /** Botón junto al valor (p. ej. la consulta del CA/OC). */
  accion?: ReactNode;
}) {
  return (
    <div>
      <Text size="xs" c="dimmed" tt="uppercase" fw={600} className="text-[10px] tracking-wide">
        {label}
      </Text>
      <div className="flex items-center gap-1">
        <Text size="sm" ff={mono ? "mono" : undefined}>
          {valor ?? ""}
        </Text>
        {accion}
      </div>
      {sub !== undefined && (
        <Text size="xs" c="dimmed">
          {sub ?? ""}
        </Text>
      )}
    </div>
  );
}

function TotalDestacado({ total, moneda }: { total: number | null; moneda: string | null }) {
  return (
    <div className="rounded-lg bg-muted p-3">
      <Group justify="space-between" align="center">
        <Text size="xs" c="dimmed" tt="uppercase" fw={600} className="text-[10px] tracking-wide">
          Total{moneda ? ` · ${moneda}` : ""}
        </Text>
        <p className="text-lg font-bold leading-none text-foreground">
          {total !== null ? formatearMonto(total, moneda, { minimumFractionDigits: 2 }) : ""}
        </p>
      </Group>
    </div>
  );
}

function ChipsMeta({ s }: { s: MiSolicitud }) {
  const mostrarNivel = !!s.flujoNivelActual && !!s.flujoCantidadNiveles && s.estado !== "Autorizada" && !TERMINAL_NEGATIVOS.has(s.estado);
  if (!mostrarNivel && !s.enviadaAContabilizacion) return null;
  return (
    <Group gap={6} wrap="wrap">
      {mostrarNivel && (
        <Badge variant="light" color="azulProfundo" size="sm">
          Nivel {s.flujoNivelActual}/{s.flujoCantidadNiveles}
        </Badge>
      )}
      {s.enviadaAContabilizacion && (
        <Badge variant="light" color="exito" size="sm">
          <IconChecks size={10} stroke={1.7} className="mr-0.5" />
          Contabilización
        </Badge>
      )}
    </Group>
  );
}
